/**
 * Utility functions for consistent date handling across the application
 * These functions ensure dates are displayed correctly without timezone shifts
 */

import moment from "moment";

/**
 * Parse a date string as local date (not UTC)
 * This prevents the timezone shift issue where dates appear one day off
 * @param dateStr - Date string in YYYY-MM-DD format
 * @returns Date object in local timezone
 */
export function parseLocalDate(dateStr: string): Date {
  if (!dateStr) return new Date();

  // Parse as local date by appending time at noon to avoid timezone issues
  // This ensures the date stays the same regardless of timezone
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day, 12, 0, 0, 0);
}

/**
 * Format a date string for display without timezone conversion
 * @param dateStr - Date string in YYYY-MM-DD format
 * @param format - Moment format string (default: "MMM D, YYYY")
 * @returns Formatted date string
 */
export function formatDateLocal(dateStr: string | Date | null | undefined, format: string = "MMM D, YYYY"): string {
  if (!dateStr) return "";

  // If it's already a Date object
  if (dateStr instanceof Date) {
    return moment(dateStr).format(format);
  }

  // If it's a string in YYYY-MM-DD format, parse it without timezone conversion
  if (typeof dateStr === "string" && dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
    // Use moment to parse as local date
    return moment(dateStr, "YYYY-MM-DD").format(format);
  }

  // For other date strings (with time), parse normally
  return moment(dateStr).format(format);
}

/**
 * Like {@link formatDateLocal} but returns `null` for empty or unparseable
 * input instead of moment's `"Invalid date"` sentinel. Keeps the sentinel
 * check in one place so callers don't string-compare it themselves.
 *
 * @param dateStr - Date string or Date (YYYY-MM-DD parsed as local).
 * @param format - moment format string.
 * @returns The formatted date, or `null` when the input is empty/invalid.
 */
export function formatDateLocalOrNull(
  dateStr: string | Date | null | undefined,
  format: string = "MMM D, YYYY"
): string | null {
  if (!dateStr) return null;
  const formatted = formatDateLocal(dateStr, format);
  return !formatted || formatted === "Invalid date" ? null : formatted;
}

/**
 * Format a date range for display
 * @param startDate - Start date string in YYYY-MM-DD format
 * @param endDate - End date string in YYYY-MM-DD format
 * @param format - Moment format string
 * @returns Formatted date range string
 */
export function formatDateRange(
  startDate: string | Date | null | undefined,
  endDate: string | Date | null | undefined,
  format: string = "MMM D, YYYY"
): string {
  if (!startDate) return "";

  const start = formatDateLocal(startDate, format);

  if (!endDate || startDate === endDate) {
    return start;
  }

  const end = formatDateLocal(endDate, format);

  // If same year, use shorter format for start date
  const startYear = moment(startDate, "YYYY-MM-DD").year();
  const endYear = moment(endDate, "YYYY-MM-DD").year();

  if (startYear === endYear) {
    const shortStart = formatDateLocal(startDate, "MMM D");
    return `${shortStart} - ${end}`;
  }

  return `${start} - ${end}`;
}

/**
 * Get formatted date from event_dates field
 * @param eventDates - The event_dates JSONB field
 * @returns Formatted date string
 */
export function getFormattedEventDates(eventDates: any): string {
  if (!eventDates || (Array.isArray(eventDates) && eventDates.length === 0)) {
    return "";
  }

  // Handle if it's a string (from database)
  let dates = eventDates;
  if (typeof dates === "string") {
    try {
      dates = JSON.parse(dates);
    } catch {
      return "";
    }
  }

  if (!Array.isArray(dates) || dates.length === 0) {
    return "";
  }

  const firstDate = dates[0];

  // Single-day event
  if (firstDate.date) {
    const formatted = formatDateLocal(firstDate.date);
    if (firstDate.startTime && firstDate.endTime) {
      return `${formatted} (${firstDate.startTime} - ${firstDate.endTime})`;
    }
    return formatted;
  }

  // Multi-day event
  if (firstDate.startDate && firstDate.endDate) {
    return formatDateRange(firstDate.startDate, firstDate.endDate);
  }

  return "";
}

/**
 * Parse event dates without timezone conversion
 * @param event - Event object with various date fields
 * @returns Object with parsed start and end dates
 */
export function parseEventDates(event: any): { startDate: Date | null; endDate: Date | null } {
  // First try event_dates field (new format)
  if (event.event_dates && Array.isArray(event.event_dates) && event.event_dates.length > 0) {
    const firstDate = event.event_dates[0];

    if (firstDate.date) {
      const date = parseLocalDate(firstDate.date);
      return { startDate: date, endDate: date };
    }

    if (firstDate.startDate && firstDate.endDate) {
      return {
        startDate: parseLocalDate(firstDate.startDate),
        endDate: parseLocalDate(firstDate.endDate),
      };
    }
  }

  // Fall back to legacy start_date/end_date fields
  if (event.start_date) {
    const startDate = parseLocalDate(event.start_date);
    const endDate = event.end_date ? parseLocalDate(event.end_date) : startDate;
    return { startDate, endDate };
  }

  return { startDate: null, endDate: null };
}

/**
 * Get a consistent date range display for an event
 * @param event - Event object
 * @returns Formatted date range string
 */
export function getEventDateDisplay(event: any): string {
  // First try the new event_dates field
  if (event.event_dates) {
    return getFormattedEventDates(event.event_dates);
  }

  // Fall back to legacy fields
  if (event.start_date) {
    return formatDateRange(event.start_date, event.end_date || event.start_date);
  }

  return "Dates TBD";
}

/**
 * Convert a date to YYYY-MM-DD format for database storage
 * @param date - Date object or string
 * @returns Date string in YYYY-MM-DD format
 */
export function toDateString(date: Date | string | null | undefined): string {
  if (!date) return "";

  if (typeof date === "string") {
    // If already in YYYY-MM-DD format, return as is
    if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return date;
    }
    // Otherwise parse and format
    return moment(date).format("YYYY-MM-DD");
  }

  return moment(date).format("YYYY-MM-DD");
}

/**
 * Check if a date string is valid
 * @param dateStr - Date string to validate
 * @returns True if valid date
 */
export function isValidDate(dateStr: string | null | undefined): boolean {
  if (!dateStr) return false;
  const date = moment(dateStr, "YYYY-MM-DD", true);
  return date.isValid();
}

/**
 * Formats a timestamp as a compact relative time string (e.g. "just now", "2m ago", "3h ago") — for
 * tight save-status indicators where `date-fns`'s longer "about 2 minutes ago" phrasing doesn't fit.
 * @param date - The timestamp to format, as a `Date` or ISO string
 * @returns A compact relative time string
 */
export function formatCompactRelativeTime(date: Date | string): string {
  const target = typeof date === "string" ? new Date(date) : date;
  const seconds = Math.floor((Date.now() - target.getTime()) / 1000);
  if (seconds < 10) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}
